// Developed by Softvative.com
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiClock } = FiIcons;

const RecentIncidents = ({ incidents }) => {
    if (!incidents || incidents.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Incidents</h3>
                <p className="text-gray-600 dark:text-gray-400">No recent incidents to display.</p>
            </div>
        );
    }

    const recentIncidents = incidents
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);

    const getPriorityStyles = (priority) => {
        switch (priority) {
            case 'Critical':
                return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
            case 'High':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
            case 'Medium':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
            default:
                return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
        }
    };

    const timeAgo = (date) => {
        if (!date) return 'Invalid date';
        let parsedDate;

        // If it's already a Date object, use it
        if (date instanceof Date) {
            parsedDate = date;
        } else {
            // Try parsing as ISO
            parsedDate = parseISO(date);
        }

        // Check if valid
        if (!isValid(parsedDate)) {
            console.warn('Invalid date:', date);
            return 'Invalid date';
        }

        return formatDistanceToNow(parsedDate, { addSuffix: true });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Incidents</h3>
                <Link
                    to="/incidents"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                >
                    <span>View All</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </Link>
            </div>

            <div className="space-y-4">
                {recentIncidents.map(incident => (
                    <div key={incident.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <Link to={`/incidents/${incident.id}`} className="font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                                    {incident.title}
                                </Link>
                                <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    <span>{incident.id}</span>
                                    <div className="flex items-center space-x-1">
                                        <SafeIcon icon={FiClock} className="w-3 h-3" />
                                        <span>{timeAgo(incident.createdAt)} ago</span>
                                    </div>
                                </div>
                            </div>
                            <div className="ml-4">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityStyles(incident.priority)}`}>
                                    {incident.priority}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
};

export default RecentIncidents;